import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/socketContext';
import { useAuth } from '../contexts/authContext';
import { API_URL } from '../lib/config';
import ProfileCreationLoader from '../components/ProfileCreationLoader';
import NewProfileCard from '../components/NewProfileCard';
import AddFriendManager from '../components/AddFriendManager';

import MatchingStatusDisplay from '../components/matching/MatchingStatusDisplay';
import PartnerProfileCard from '../components/matching/PartnerProfileCard';
import MatchingActions from '../components/matching/MatchingActions';
// import ProfilePromptCard from '../components/matching/ProfilePromptCard';
import CommonButton from '../components/CommonButton';

import './MatchingPage.css';
import './ProfilePage.css';

function MatchingPage() {
    const { currentUser } = useAuth();
    const [matchingStatus, setMatchingStatus] = useState('새로운 매칭 시작 버튼을 눌러 매칭을 시도하세요.');
    const [userStatus, setUserStatus] = useState('active');
    const [suspendedUntil, setSuspendedUntil] = useState(null);
    const [partnerProfile, setPartnerProfile] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [partnerSocketId, setPartnerSocketId] = useState(null);
    const [isMatchingInProgress, setIsMatchingInProgress] = useState(false);
    const [isCreatingProfile, setIsCreatingProfile] = useState(false);
    const [isSettingDefaultProfile, setIsSettingDefaultProfile] = useState(false);
    const navigate = useNavigate();
    const socket = useSocket();
    const hasCheckedProfileRef = useRef(false);
    const [showProfileCreatePrompt, setShowProfileCreatePrompt] = useState(false);
    const [promptChatRoomId, setPromptChatRoomId] = useState(null);
    const [profileCreationData, setProfileCreationData] = useState(null);
    const [showNewlyCreatedProfile, setShowNewlyCreatedProfile] = useState(false);
    const [newlyCreatedProfileData, setNewlyCreatedProfileData] = useState(null);

    const formatChatTimestamp = (isoString) => {
        if (!isoString) return '정보 없음';
        try {
            const date = new Date(isoString);
            return `${date.getFullYear()}년 ${(date.getMonth() + 1).toString().padStart(2, '0')}월 ${date.getDate().toString().padStart(2, '0')}일 ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        } catch (e) { return '날짜 오류'; }
    };

    useEffect(() => {
        if (!socket || !currentUser) return;

        const handleSocketConnect = () => {
            // console.log("소켓 연결 감지됨 (MatchingPage)");
            setIsConnected(true);
            // 혹시라도 에러 상태였다면 다시 대기 메시지로 복구
            setMatchingStatus((prev) => 
                prev.includes('오류') ? '새로운 매칭 시작 버튼을 눌러 매칭을 시도하세요.' : prev
            );
        };
        
        const handleSocketDisconnect = () => {
            // console.warn("소켓 연결 끊김 (MatchingPage)");
            setIsConnected(false);
            setIsMatchingInProgress(false); // 연결 끊기면 매칭 중 상태도 해제
        };

        const handleConnectError = (err) => {
            setMatchingStatus('연결 오류 발생. 다시 시도해주세요.');
            setIsConnected(false);
            setIsMatchingInProgress(false);
        };

        // if (socket.connected) handleSocketConnect();
        // else socket.on('connect', handleSocketConnect);

        socket.on('connect', handleSocketConnect);
        socket.on('disconnect', handleSocketDisconnect);
        socket.on('connect_error', handleConnectError);

        // 2. [핵심] 초기 상태 체크 및 강제 연결 시도
        if (socket.connected) {
            handleSocketConnect();
        } else {
            // 연결이 안 되어 있다면 연결 시도 (로그인 직후 토큰 갱신 이슈 대비)
            // console.log("소켓이 연결되지 않음. 수동 연결 시도...");
            socket.connect();
        }

        socket.on('matching_status', (data) => {
            setMatchingStatus(data.message);

            // ★ 핵심: 에러나 거절, 취소 상태라면 UI를 '대기 중'에서 '초기 상태'로 풀어줘야 함
            if (data.type === 'error' || data.type === 'rejected' || data.type === 'cancelled') {
                setIsMatchingInProgress(false); // 로딩 끄기 (노란 버튼 -> 회색 버튼)
                setPartnerProfile(null);
                // 필요하다면 alert로 한 번 더 확실히 알려주기
                // alert(data.message); 
            }
        });
        socket.on('matching_proposal', (data) => {
            setMatchingStatus('상대방과 매칭이 성사되었습니다. 프로필을 확인하고 수락/거절하세요.');
            setPartnerProfile(data.partner);
            setPartnerSocketId(data.partner.socketId);
            setIsMatchingInProgress(true);
        });
        socket.on('match_accepted', (data) => {
            setIsMatchingInProgress(false);
            navigate('/chat', { state: { chatRoomId: data.chatRoomId, partner: data.partner } });
        });
        
        const resetMatchingState = (message) => {
            alert(message);
            setMatchingStatus(message + ' 새로운 매칭을 시도해주세요.');
            setPartnerProfile(null);
            setPartnerSocketId(null);
            setIsMatchingInProgress(false);
        };
        socket.on('match_rejected', (data) => resetMatchingState(data.message));
        socket.on('match_cancelled', (data) => resetMatchingState(data.message));
        socket.on('partner_disconnected', (data) => resetMatchingState(data.message + " 매칭이 취소됩니다."));

        const handleProfileCreationFeedback = ({ success, message, profile }) => {
            setIsCreatingProfile(false);
            setShowProfileCreatePrompt(false);
            if (success) {
                setMatchingStatus('당신의 새로운 AI 프로필이 완성되었습니다! 확인해주세요.');
                setNewlyCreatedProfileData(profile);
                setShowNewlyCreatedProfile(true);
            } else {
                alert(`프로필 생성 실패: ${message}`);
            }
        };

        // [수정] 안내창 표시 정책을 적용한 핸들러
        const handleAiProfileAnalysisResult = (data) => {
            // 1. 프로필 생성이 가능한 경우 -> 항상 창을 띄움
            if (data.action === 'PROMPT_FOR_PROFILE' && data.sourceChatRoomId) {
                setPromptChatRoomId(data.sourceChatRoomId);
                setProfileCreationData({ canCreate: true, ...data });
                setShowProfileCreatePrompt(true);
                return;
            }
            // 2. 에러가 발생했고, 채팅방 ID가 있으며, 최소 길이가 미충족된 경우 -> 항상 창을 띄움
            if (data.action === 'ERROR' && data.sourceChatRoomId && !data.minLengthSatisfied) {
                setPromptChatRoomId(data.sourceChatRoomId);
                setProfileCreationData({ canCreate: false, ...data });
                setShowProfileCreatePrompt(true);
                return;
            }
            // 3. 그 외 모든 경우 (쿨타임 중, 분석할 채팅방 없음 등) -> 창을 띄우지 않음
            hasCheckedProfileRef.current = true;
            setShowProfileCreatePrompt(false);
        };

        socket.on('profile_creation_feedback', handleProfileCreationFeedback);
        socket.on('profile_prompt_response', handleAiProfileAnalysisResult);

        const userId = localStorage.getItem('currentUserId');
        if (socket.connected && !hasCheckedProfileRef.current && userId) {
            socket.emit('check_chat_for_profile', { userId });
            hasCheckedProfileRef.current = true;
        }

        return () => {
            socket.off('connect', handleSocketConnect);
            socket.off('connect_error', handleConnectError);
            socket.off('matching_status');
            socket.off('matching_proposal');
            socket.off('match_accepted');
            socket.off('match_rejected');
            socket.off('match_cancelled');
            socket.off('partner_disconnected');
            socket.off('profile_creation_feedback');
            socket.off('profile_prompt_response');
            if (socket && socket.connected) {
                // 서버에 "나 대기열에서 빼줘" 라고 신호 보냄
                socket.emit('cancel_matching'); 
            }
        };
    }, [navigate, socket, currentUser]);

    const handleResponse = (response) => {
        if (!socket || !partnerSocketId) return;
        socket.emit('matching_response', { response, partnerSocketId });
        if (response === 'reject') {
            setMatchingStatus('매칭을 거절했습니다. 새로운 매칭 시작 버튼을 눌러 다시 시도해주세요.');
            setPartnerProfile(null);
            setIsMatchingInProgress(false);
        } else {
            setMatchingStatus('상대방의 수락을 기다리는 중...');
        }
    };
    
    const handleGoHome = () => {
        if (socket && (isMatchingInProgress || partnerProfile)) {
            socket.emit('cancel_matching');
        }
        navigate('/');
    };

    const handleCancelMatching = () => {
        if (socket) {
            socket.emit('cancel_matching');
            setMatchingStatus('매칭 대기 취소됨. 새로운 매칭 시작 버튼을 눌러 매칭을 시도하세요.');
            setPartnerProfile(null);
            setIsMatchingInProgress(false);
        }
    };

    useEffect(() => {
        const fetchUserProfileAndStatus = async () => {
            if (!currentUser) return;

            try {
                const idToken = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/login/me`, {
                    headers: { 'Authorization': `Bearer ${idToken}` }
                });
                const data = await response.json();

                if (response.ok && data.user) {
                    // 1. 유저 상태 업데이트 (기존 로직)
                    setUserStatus(data.user.status);
                    setSuspendedUntil(data.user.suspendedUntil);

                    // 2. ★ [추가] 프로필 정보 찾아서 로컬 스토리지 강제 주입! ★
                    const repProfile = data.user.profiles?.find(p => p.isRepresentative) || data.user.profiles?.[0];

                    if (repProfile) {
                        // 정보가 비어있을 때만, 혹은 강제로 최신화
                        localStorage.setItem('userNickname', repProfile.nickname);
                        localStorage.setItem('userCharacter', repProfile.character);
                        localStorage.setItem('currentUserId', data.user.id);
                        
                        // console.log("페이지 진입 동기화 완료:", repProfile.nickname);

                        // 3. ★ 핵심: 상태를 건드려줘야 화면이 다시 그려지면서 버튼이 활성화됨
                        setMatchingStatus('새로운 매칭 시작 버튼을 눌러 매칭을 시도하세요.');
                    }
                }
            } catch (error) {
                console.error("사용자 정보 동기화 실패:", error);
            }
        };

        fetchUserProfileAndStatus();
    }, [currentUser]);

    // const startNewMatching = () => {
    //     if (userStatus === 'banned') {
    //         alert("계정이 정지되어 매칭을 시작할 수 없습니다.\n정지 만료일: UNDEFINED");
    //         return;
    //     }
    //     if (userStatus === 'suspended') {
    //         const endDate = new Date(suspendedUntil).toLocaleString('ko-KR');
    //         alert(`계정이 정지되어 매칭을 시작할 수 없습니다.\n정지 만료일: ${endDate}`);
    //         return;
    //     }
    //     if (!socket || !socket.connected) return alert('연결 오류: 잠시 후 다시 시도해주세요.');
    //     if (isMatchingInProgress || partnerProfile) return;

    //     const userNickname = localStorage.getItem('userNickname');
    //     const userCharacter = localStorage.getItem('userCharacter');
    //     const currentUserId = localStorage.getItem('currentUserId');
    //     if (userNickname && userCharacter && currentUserId) {
    //         setMatchingStatus('매칭 대기 중...');
    //         setIsMatchingInProgress(true);
    //         socket.emit('start_matching', { userId: currentUserId, nickname: userNickname, character: userCharacter });
    //     } else {
    //         alert('프로필 정보가 없습니다. 홈으로 돌아갑니다.');
    //         navigate('/');
    //     }
    // };
    // [수정] 매칭 시작 함수 (비동기 처리 추가)
    // [추가] 실제 소켓 이벤트를 전송하는 내부 함수 (실행 보장용)
    const emitMatchingRequest = (userId, nickname, character) => {
        // console.log(`[Matching] 매칭 요청 시작: ${userId}, ${nickname}`);
        setMatchingStatus('매칭 대기 중...');
        setIsMatchingInProgress(true);
        socket.emit('start_matching', { userId, nickname, character });
    };

    // [수정] 매칭 시작 버튼 핸들러 (데이터 준비 -> 실행 연결)
    const startNewMatching = async () => {
        // 1. 기본 상태 체크
        if (userStatus === 'banned') {
            alert("계정이 정지되어 매칭을 시작할 수 없습니다.\n정지 만료일: UNDEFINED");
            return;
        }
        if (userStatus === 'suspended') {
            const endDate = new Date(suspendedUntil).toLocaleString('ko-KR');
            alert(`계정이 정지되어 매칭을 시작할 수 없습니다.\n정지 만료일: ${endDate}`);
            return;
        }
        if (!socket || !socket.connected) return alert('연결 오류: 잠시 후 다시 시도해주세요.');
        if (isMatchingInProgress || partnerProfile) return;

        const getSafeItem = (key) => {
            const value = localStorage.getItem(key);
            if (!value || value === 'undefined' || value === 'null') return null;
            return value;
        };

        // 2. 안전하게 정보 가져오기
        let userNickname = getSafeItem('userNickname');
        let userCharacter = getSafeItem('userCharacter');
        let currentUserId = getSafeItem('currentUserId');

        // 2. 로컬 스토리지 정보 가져오기
        // let userNickname = localStorage.getItem('userNickname');
        // let userCharacter = localStorage.getItem('userCharacter');
        // let currentUserId = localStorage.getItem('currentUserId');

        // 3. 정보가 모두 있다면? -> 즉시 실행! 🚀
        if (userNickname && userCharacter && currentUserId) {
            emitMatchingRequest(currentUserId, userNickname, userCharacter);
            return;
        }

        // 4. 정보가 없다면? -> 서버에서 가져오기 (비동기)
        // console.log("프로필 정보 누락. 서버 조회 시작...");
        setMatchingStatus("서버에서 프로필 정보를 불러오는 중..."); // 로딩 표시

        try {
            if (!currentUser) {
                alert('로그인 정보가 없습니다. 다시 로그인해주세요.');
                navigate('/');
                return;
            }

            const idToken = await currentUser.getIdToken();
            const response = await fetch(`${API_URL}/api/login/me`, {
                headers: { 'Authorization': `Bearer ${idToken}` }
            });
            
            const data = await response.json();
            
            if (response.ok && data.user) {
                // 대표 프로필 찾기
                const repProfile = data.user.profiles?.find(p => p.isRepresentative) || data.user.profiles?.[0];
                
                if (repProfile) {
                    // 데이터 확보 성공
                    const fetchedNickname = repProfile.nickname;
                    const fetchedCharacter = repProfile.character;
                    const fetchedUserId = data.user.id;

                    // 스토리지 복구
                    localStorage.setItem('userNickname', fetchedNickname);
                    localStorage.setItem('userCharacter', fetchedCharacter);
                    localStorage.setItem('currentUserId', fetchedUserId);
                    
                    // console.log("프로필 복구 완료. 매칭 자동 시작.");

                    // ★★★ [핵심] 데이터 가져오자마자 강제로 매칭 실행 함수 호출! ★★★
                    emitMatchingRequest(fetchedUserId, fetchedNickname, fetchedCharacter);
                    
                } else {
                    throw new Error('프로필 데이터 없음');
                }
            } else {
                throw new Error('유저 정보 조회 실패');
            }
        } catch (error) {
            console.error("프로필 재조회 실패:", error);
            setMatchingStatus('새로운 매칭 시작 버튼을 눌러 매칭을 시도하세요.'); // 에러 시 상태 원복
            alert('프로필 정보를 찾을 수 없습니다. 다시 시도해주세요.');
        }
    };

    const handleCreateProfile = () => {
        const currentUserId = localStorage.getItem('currentUserId');
        if (!socket || !currentUserId || !promptChatRoomId) {
            alert("프로필 분석 요청에 필요한 정보가 부족합니다.");
            setIsCreatingProfile(false);
            return;
        }

        setIsCreatingProfile(true);
        socket.emit('request_ai_profile_analysis', { 
            userId: currentUserId, 
            chatRoomId: promptChatRoomId 
        });
    };

    const handleClosePrompt = () => {
        setShowProfileCreatePrompt(false);
        setProfileCreationData(null);
        setPromptChatRoomId(null);
    };
    
    const handleConfirmAndDeleteChat = () => {
        const currentUserId = localStorage.getItem('currentUserId');
        if (socket && promptChatRoomId && currentUserId) {
            if (process.env.NODE_ENV === 'development') {
                console.log('채팅방 삭제요청완료');
            }
            socket.emit('delete_insufficient_chat', { chatRoomId: promptChatRoomId, userId: currentUserId });
        }
        else{
            if (process.env.NODE_ENV === 'development') {
                console.log('채팅방 삭제요청이 되지 않았습니다.',socket, promptChatRoomId,currentUserId);
            }
        }
        handleClosePrompt();
    };

    const handleSetAsDefaultProfile = async () => {
        if (!newlyCreatedProfileData || !newlyCreatedProfileData.id) {
            alert("대표 프로필로 설정할 프로필 정보를 찾을 수 없습니다.");
            return;
        }
        if (!currentUser) {
            alert("인증 정보가 없습니다. 다시 로그인해주세요.");
            return;
        }
        setIsSettingDefaultProfile(true);
        try {
            const profileId = newlyCreatedProfileData.id;
            const idToken = await currentUser.getIdToken();
            const response = await fetch(`${API_URL}/api/profiles/profile/set-representative/${profileId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`,
                },
            });

            const data = await response.json(); // [추가] 응답 데이터를 받습니다.
            if (!response.ok) {
                // const errorData = await response.json();
                throw new Error(data.message || '대표 프로필 설정에 실패했습니다.');
            }

            // ★★★ 핵심 수정: localStorage를 새로운 대표 프로필 정보로 업데이트합니다. ★★★
            if (data.profiles) {
                const newRepProfile = data.profiles.find(p => p.isRepresentative);
                if (newRepProfile) {
                    localStorage.setItem('userNickname', newRepProfile.nickname);
                    localStorage.setItem('userCharacter', newRepProfile.character);
                    if (process.env.NODE_ENV === 'development') {
                        console.log('localStorage 업데이트 완료:', newRepProfile.nickname);
                    }
                }
            }
            // ★★★ 여기까지 ★★★

            alert('대표 프로필로 설정되었습니다!');
            setShowNewlyCreatedProfile(false);
            setMatchingStatus('새로운 매칭 시작 버튼을 눌러 매칭을 시도하세요.');
        } catch (e) {
            console.error("대표 프로필 설정 오류:", e);
            alert(`대표 프로필 설정 중 오류가 발생했습니다: ${e.message}`);
        } finally {
            setIsSettingDefaultProfile(false);
        }
    };
    
    const resetToStartMatching = () => {
        setShowNewlyCreatedProfile(false);
        setNewlyCreatedProfileData(null);
        setMatchingStatus('새로운 매칭 시작 버튼을 눌러 매칭을 시도하세요.');
        setPartnerProfile(null);
        setPartnerSocketId(null);
        setIsMatchingInProgress(false);
        hasCheckedProfileRef.current = true;
    };

    const handleViewChatHistory = () => {
        if (promptChatRoomId && profileCreationData) {
            
            // 1. 현재 모달 상태 저장
            const stateToSave = {
                data: profileCreationData,
                roomId: promptChatRoomId
            };
            sessionStorage.setItem('restore_modal_data', JSON.stringify(stateToSave));

            // 2. 채팅방으로 이동
            navigate('/chat', { 
                state: { 
                    chatRoomId: promptChatRoomId, 
                    partner: { 
                        nickname: profileCreationData.partnerNickname || '알 수 없음',
                    },
                    from: '/matching',
                    isReadOnly: true
                } 
            });
        }
    };

    const renderContent = () => {
        if (isCreatingProfile) return <ProfileCreationLoader />;
        if (showProfileCreatePrompt && profileCreationData) {
            const isInsufficient = profileCreationData.canCreate === false;

            return (
                <div className="modal-overlay">
                    <div className="profile-card matching-prompt-card"
                    style={{
                        maxWidth: '400px',
                        margin: '0 auto',
                        position: 'relative'
                        }}>

                        <h2>{isInsufficient ? "프로필 생성 불가" : "최근 채팅으로 프로필을 생성하시겠습니까?"}</h2>
                        
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: '8px',
                            marginBottom: '15px' // 간격 조정
                        }}>
                            <h3 style={{ margin: 0 }}>
                                상대방: {profileCreationData.partnerNickname || '알 수 없음'}
                            </h3>

                            {/* partnerId가 데이터에 있을 때만 버튼 표시 */}
                            {profileCreationData.partnerId && (
                                <AddFriendManager
                                    partnerId={profileCreationData.partnerId}
                                    defaultNickname={profileCreationData.partnerNickname}
                                />
                            )}
                        </div>

                        <p>마지막 채팅: {formatChatTimestamp(profileCreationData.lastChatTimestamp)}</p>
                        
                        {/* 메시지 내용 박스 */}
                        <div className="last-message-button"
                            onClick={handleViewChatHistory}
                            style={{ 
                            cursor: 'pointer',       // 마우스 올리면 손가락 모양
                            // pointerEvents: 'none' // ★ 이 줄을 삭제해야 클릭이 됩니다!
                        }}>
                            <p className="last-message-label">마지막 메시지:</p>
                            <p className="last-message-content">"{profileCreationData.lastMessageText || '내용 없음'}"</p>
                        </div>

                        {isInsufficient && (
                            <p style={{ color: '#ff6b6b', fontSize: '0.9rem', marginTop: '15px', marginBottom: '0', textAlign: 'center' }}>
                                대화 내용이 너무 짧아 프로필을 생성할 수 없습니다.<br/>
                            </p>
                        )}
                        
                        <div className="modal-buttons">
                            {isInsufficient ? (
                                <CommonButton 
                                    text="확인"
                                    variant="secondary"
                                    onClick={handleConfirmAndDeleteChat}
                                    className="modal-action-btn" // Width 조절용 클래스
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <>
                                    <CommonButton 
                                        text="예" 
                                        variant="primary" 
                                        onClick={handleCreateProfile}
                                        className="modal-action-btn"
                                        // 인라인 스타일로 flex: 1 효과 주기 (CommonButton에 style props가 없다면 className으로 해결해야 함)
                                    />
                                    <CommonButton 
                                        text="아니오" 
                                        variant="secondary" 
                                        onClick={handleClosePrompt}
                                        className="modal-action-btn"
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        if (showNewlyCreatedProfile && newlyCreatedProfileData) {
            return (
                <NewProfileCard profile={newlyCreatedProfileData}>
                    <button
                        className="npc-button npc-button-primary"
                        onClick={handleSetAsDefaultProfile}
                        disabled={isSettingDefaultProfile}
                    >
                        {isSettingDefaultProfile ? '설정 중...' : '대표 프로필로 설정하고 매칭 시작하기'}
                    </button>
                    <button
                        className="npc-button npc-button-secondary"
                        onClick={resetToStartMatching}
                    >
                        설정 없이 매칭 시작하기
                    </button>
                </NewProfileCard>
            );
        }
        return (
            <>
                <h1>다른 유저와 매칭</h1>
                <MatchingStatusDisplay status={matchingStatus} />
                
                {partnerProfile ? (
                    <PartnerProfileCard
                        profile={partnerProfile}
                        onAccept={() => handleResponse('accept')}
                        onReject={() => handleResponse('reject')}
                    />
                ) : (
                    <MatchingActions
                        isMatching={isMatchingInProgress}
                        isConnected={isConnected}
                        onStart={startNewMatching}
                        onCancel={handleCancelMatching}
                    />
                )}

                {!isMatchingInProgress && (
                    <CommonButton 
                        text="홈으로 돌아가기" 
                        variant="secondary" 
                        onClick={handleGoHome}
                        className="back-button" // 위치(margin) 잡는 CSS 클래스 유지
                    />
                )}
            </>
        );
    };

    return (
        <div className={`matching-page-container ${isMatchingInProgress ? 'matching-active' : ''}`}>
            <div id="stars"></div>
            <div id="stars2"></div>
            <div id="stars3"></div>
            {renderContent()}
        </div>
    );
}

export default MatchingPage;